import { useLocation, useNavigate } from 'react-router-dom';
import { useCallback, useEffect } from 'react';

const REDIRECT_KEY = 'printdeck_redirect_after_login';

/**
 * Save the intended destination before redirecting to login
 */
export const saveIntendedDestination = (pathname: string, search: string = ''): void => {
  sessionStorage.setItem(REDIRECT_KEY, JSON.stringify({ pathname, search }));
};

/**
 * Get and clear the intended destination
 */
export const getIntendedDestination = (defaultPath: string = '/dashboard'): string => {
  try {
    const stored = sessionStorage.getItem(REDIRECT_KEY);
    if (stored) {
      const { pathname, search } = JSON.parse(stored);
      sessionStorage.removeItem(REDIRECT_KEY);
      return `${pathname}${search}`;
    }
  } catch (error) {
    console.error('Failed to parse intended destination:', error);
    sessionStorage.removeItem(REDIRECT_KEY);
  }
  return defaultPath;
};

/**
 * Custom hook to handle post-login redirect to intended destination
 * Usage in LoginPage after successful login:
 *   const destination = useRedirectAfterLogin();
 *   navigate(destination);
 */
export const useRedirectAfterLogin = (defaultPath: string = '/dashboard'): string => {
  return getIntendedDestination(defaultPath);
};

/**
 * Custom hook to detect and handle token expiry
 * Emits a custom event that can be listened to for UI notifications
 */
export const useTokenExpiryHandler = (): void => {
  const navigate = useNavigate();

  useEffect(() => {
    const handleTokenExpiry = () => {
      // Dispatch custom event so UI can show notification
      window.dispatchEvent(new CustomEvent('auth:token-expired'));
      
      // Save current location as intended destination
      const location = window.location.pathname + window.location.search;
      if (location !== '/login' && location !== '/signup' && location !== '/forgot-password') {
        saveIntendedDestination(window.location.pathname, window.location.search);
      }
      
      // Navigate to login
      navigate('/login', { replace: true });
    };

    window.addEventListener('auth:token-expired', handleTokenExpiry);
    return () => window.removeEventListener('auth:token-expired', handleTokenExpiry);
  }, [navigate]);
};

/**
 * Custom hook to listen for token expiry events and show notifications
 */
export const useTokenExpiryNotification = (
  onExpired?: () => void
): void => {
  useEffect(() => {
    const handleTokenExpired = () => {
      onExpired?.();
      // You can dispatch a toast notification here if needed
      console.warn('Token has expired. Please log in again.');
    };

    window.addEventListener('auth:token-expired', handleTokenExpired);
    return () => window.removeEventListener('auth:token-expired', handleTokenExpired);
  }, [onExpired]);
};
